import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import type { IDashboard } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/interfaces/dashboard.interface';
import type { IDashboardRouteOptions } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/interfaces/dashboard-route-options.inteface';
import type { IDashboardSettings } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/interfaces/dashboard-settings.interface';
import { DashboardSettingsService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/services/dashboards-settings.service';
import { DashboardService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/services/dashboards.service';
import { FilterDemoTourService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/services/filter-demo-tour.service';
import {
  ITileItemDrawerRequest,
  TileItemDrawerService,
} from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/services/tile-item-drawer.service';
import { FiltersChannel } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/filters/models/filters-channel.model';
import { FiltersSidenavService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/filters/services/filters-sidenav.service';
import { IBreadcrumbLink } from '@suvo/frontend-framework/SUVO-CORE-CLIENT-LIB/projects/suvo-core-client-lib/src/lib/features/breadcrumbs/interfaces/breadcrumb-link.interface';
import { ProductService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/product/services/product.service';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DynamicRouteService, IDynamicRouteData } from '../../../../../dynamic-routes';

@Component({
  standalone: false,
  selector: 'suvo-bi-dashboard-screen',
  templateUrl: './dashboard-screen.component.html',
  styleUrl: './dashboard-screen.component.scss',
})
export class DashboardScreenComponent implements OnInit, OnDestroy {
  dashboard: IDashboard & { canView?: boolean };
  dashboardSettings: IDashboardSettings;
  dataSource: string; // For the unsubscribed screen.
  showErrorMessage: string;
  filtersChannel: FiltersChannel;
  hideRightHandFilters: boolean;

  filterGroup: string;

  exportDisabled = true;

  breadcrumbs: IBreadcrumbLink[];

  filtersSidenavMode: MatDrawerMode = 'side';
  filtersSidenavOpenedByDefault = true;
  selectedTileItem: ITileItemDrawerRequest | null = null;
  selectedTileItemTitle = '';
  selectedTileItemTitleLoading = false;
  selectedTileItemHasProjectNameProperty: boolean | null = null;

  @ViewChild('filtersSidenav') filtersSidenav: MatSidenav;

  private readonly unsubscribe$ = new Subject<void>();

  /**
   * @author Aidan
   * @todo Hacky workaround for now so that I don't have to modify the framework and introduce side
   *   effects. The DashboardComponent isn't reactive to its inputs, so I'll just force a remount
   *   when the activated route data changes.
   */
  forceUnmount = false;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly dashboardService: DashboardService,
    private readonly dashboardSettingsService: DashboardSettingsService,
    private readonly dynamicRoutes: DynamicRouteService,
    private readonly filterDemoTourService: FilterDemoTourService,
    private readonly filtersSidenavService: FiltersSidenavService,
    private readonly productService: ProductService,
    private readonly tileItemDrawerService: TileItemDrawerService,
  ) {
    this.dashboardSettings = {
      canEdit: this.dashboardSettingsService.canEditDashboards,
      canEditJson: this.dashboardSettingsService.canEditDashboards,
    };
  }

  ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
      // Route data changes indicate dashboard-screen navigation within this shell.
      this.closeTileItemDrawer();
      this.routeDataChanged(data.dynamicRoute);
    });

    if (
      this.breakpointObserver.isMatched([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
    ) {
      this.filtersSidenavOpenedByDefault = false;
      this.filtersSidenavService.setActive(false, false);
    } else {
      this.filtersSidenavOpenedByDefault = true;
      this.filtersSidenavService.setActive(true, true);
    }

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((result) => {
        if (result.matches) {
          this.filtersSidenavMode = 'over';
        } else {
          this.filtersSidenavMode = 'side';
        }
        this.changeDetectorRef.detectChanges();
      });

    this.tileItemDrawerService.openRequests$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((request) => {
        this.selectedTileItem = request;
        this.selectedTileItemTitle = '';
        this.selectedTileItemTitleLoading = true;
        this.selectedTileItemHasProjectNameProperty = null;
        this.changeDetectorRef.detectChanges();
      });
  }

  ngAfterViewInit() {
    this.filtersSidenavService.sidenavSubject
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: any) => {
        setTimeout(() => {
          if (this.filtersSidenav) {
            state.open ? this.filtersSidenav.open() : this.filtersSidenav.close();
          }
        });
      });

    if (this.filtersSidenav) {
      this.filtersSidenav.openedChange
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((filtersSidenavOpen) => {
          setTimeout(() => {
            if (this.filtersSidenavService?.sidenavOpen != filtersSidenavOpen) {
              this.filtersSidenavService?.setOpen(filtersSidenavOpen);
            }
          });
        });
    }
  }

  ngOnDestroy() {
    this.closeTileItemDrawer();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  closeTileItemDrawer() {
    this.selectedTileItem = null;
    this.selectedTileItemTitle = '';
    this.selectedTileItemTitleLoading = false;
    this.selectedTileItemHasProjectNameProperty = null;
  }

  onTileItemTitleResolved(title: string) {
    const nextTitle = String(title ?? '').trim();
    this.selectedTileItemTitle = nextTitle;
    this.selectedTileItemTitleLoading = false;
  }

  onItemNamePropertyConfigured(isConfigured: boolean) {
    this.selectedTileItemHasProjectNameProperty = isConfigured;

    if (!isConfigured) {
      this.selectedTileItemTitleLoading = false;
    }
  }

  private async routeDataChanged(data: IDynamicRouteData) {
    const dashboardId = data.variant?.dashboardUuid;

    if (dashboardId) {
      this.loadDashboard(
        dashboardId,
        data.current.routeType === 'EXPORT'
          ? {
              filterGroup: data.current.filterGroup,
              filterLayout: {
                type: 'accordion',
                autoApplyFilters: true,
              },
            }
          : undefined,
      );
    } else {
      this.showErrorMessage = 'This route does not include any page content.';
      this.dashboard = null;
    }

    this.filterGroup = data.filterGroup;
    this.filtersChannel = data.filtersChannel;

    if (this.filtersChannel && this.dashboardSettingsService.filtersButtonTour) {
      this.filterDemoTourService.startFiltersDemoTour();
    }

    this.breadcrumbs = data.branch
      .filter((node) => this.dynamicRoutes.shouldShowNodeInBreadcrumb(node))
      .map((node) => {
        const variant = this.dynamicRoutes.resolveVariant(node);
        return {
          displayName: variant.name,
          route: `/private${variant.pathFull}`,
        };
      });

    this.exportDisabled =
      !this.filtersChannel ||
      !this.productService.isFeatureEnabled('EXPORT', [
        'DATASOURCE:' + this.filtersChannel.channelName,
      ]);
  }

  private async loadDashboard(dashboardBaseId: string, extraOptions?: IDashboardRouteOptions) {
    this.dashboard = await this.dashboardService.getLatestDashboardByBaseId(dashboardBaseId);

    if (this.dashboard) {
      this.dashboardSettings.canFilter = extraOptions?.filterGroup ? true : false;
      this.dashboardSettings.filterGroup = extraOptions?.filterGroup;
      this.dashboardSettings.filterLayout = extraOptions?.filterLayout;
      this.dashboardSettings.exportRoute = extraOptions?.exportRoute;
      await this.dashboardService.processTilesAgainstSubs(this.dashboard);
      this.dataSource = this.dashboard.displayName;
      this.showErrorMessage = null;
    } else {
      this.setErrorMessage(`No such dashboard with id ${dashboardBaseId} exists.`);
    }

    this.forceUnmount = true;
    setTimeout(() => (this.forceUnmount = false));
  }

  private setErrorMessage(message: string) {
    this.dashboard = null;
    this.showErrorMessage = message;
  }
}
